import React, { ReactNode, useEffect, useState } from 'react'
import { Box, Button, Menu, MenuItem, Tooltip, Typography, Checkbox } from '@mui/material'
import { CloseIcon, ArrowIcon } from '@/src/assets/icons'

interface OptionProps {
  id?: number | string
  name: number | string
  value?: number | string | object
  disable?: boolean
  type?: string
  // Shown as a hover tooltip on the menu item (e.g. classification
  // definitions on the Buyer / Seller filter).
  description?: string
}

interface FilterProps {
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  label?: string
  options?: OptionProps[]
  multiple?: boolean
  fieldClass?: string
  divClass?: string
  minWidth?: string
  minHeight?: string
  defaultValue?: string | number | any
  onSelectionChange?: (selected: any) => void
  disabled?: boolean
  menuMinWidth?: number | string
  menuMaxWidth?: number | string
  menuMaxHeight?: number | string
  wrapOptionText?: boolean
  // Optional content pinned below the option list (e.g. a combination
  // mode toggle). Rendered inside the menu, separated by a divider.
  menuFooter?: ReactNode
  emptyText?: string
}

const SelectFilter: React.FC<FilterProps> = ({
  leftIcon,
  rightIcon,
  label = 'Select',
  options = [],
  multiple = false,
  fieldClass = '',
  divClass = '',
  minWidth = '',
  minHeight = '',
  defaultValue,
  onSelectionChange,
  disabled = false,
  menuMinWidth,
  menuMaxWidth,
  menuMaxHeight,
  wrapOptionText = false,
  menuFooter,
  emptyText = 'No options available'
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOptions, setSelectedOptions] = useState<OptionProps[]>(() => {
    if (defaultValue) {
      const defaultOption = options.find(
        opt => opt.value === defaultValue || opt.id === defaultValue
      )
      return defaultOption ? [defaultOption] : []
    }
    return []
  })

  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleOptionSelect = (option: OptionProps) => {
    if (option.disable) return

    let newSelection: OptionProps[]

    if (multiple) {
      const isSelected = selectedOptions.some(selected => selected.id === option.id)
      if (isSelected) {
        newSelection = selectedOptions.filter(selected => selected.id !== option.id)
      } else {
        newSelection = [...selectedOptions, option]
      }
      // Don't close menu for multiple selection
    } else {
      newSelection = [option]
      handleClose() // Close menu for single selection
    }

    setSelectedOptions(newSelection)

    if (onSelectionChange) {
      if (multiple) {
        onSelectionChange(newSelection.map(item => item.id))
      } else {
        onSelectionChange(newSelection.length > 0 ? newSelection[0].id : null)
      }
    }
  }

  const getDisplayText = () => {
    if (selectedOptions.length === 0) return label

    if (multiple) {
      return selectedOptions.length === 1
        ? String(selectedOptions[0].name)
        : `${String(selectedOptions[0].name)} + ${selectedOptions.length - 1} `
    }

    return String(selectedOptions[0].name)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newSelection: OptionProps[] = []
    setSelectedOptions(newSelection)
    onSelectionChange?.(newSelection)
  }

  const isOptionSelected = (option: OptionProps) => {
    return selectedOptions.some(selected => selected.id === option.id)
  }

  useEffect(() => {
    if (disabled) {
      setSelectedOptions([])
    }
  }, [disabled])

  useEffect(() => {
    if (defaultValue && Array.isArray(defaultValue)) {
      const newSelected = options.filter(opt => defaultValue.includes(opt.id))
      setSelectedOptions(newSelected)
    }
  }, [defaultValue, options])

  return (
    <Box className={divClass}>
      <Button
        disabled={disabled}
        variant="outlined"
        onClick={handleClick}
        className={fieldClass}
        sx={{
          borderRadius: '999px',
          backgroundColor: selectedOptions.length > 0 ? '#E4E1F1' : 'transparent',
          borderColor: '#1B1F3B',
          // color: "#1B1F3B",
          // fontWeight: 500,
          textTransform: 'none',
          paddingX: 2.5,
          paddingY: 1,
          minHeight: minHeight ? minHeight : '44px',
          minWidth: minWidth ? minWidth : '161px',
          justifyContent: 'space-between',
          '&:hover': {
            backgroundColor: selectedOptions.length > 0 ? '#DAD6EB' : '#f5f5f5',
            borderColor: '#1B1F3B'
          }
        }}
        endIcon={
          selectedOptions?.length > 0 ? (
            <div onClick={handleClear}>
              <CloseIcon className="w-[20px] h-[20px] cursor-pointer fill-primary-main" />
            </div>
          ) : rightIcon ? (
            rightIcon
          ) : (
            <ArrowIcon className="w-[12px] h-[12px] rotate-90 fill-primary-main" />
          )
        }
        startIcon={leftIcon}
      >
        <Typography variant="buttonLarge_lg" color={disabled ? 'gray' : 'primary.main'}>
          {getDisplayText()}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              marginTop: 8,
              borderRadius: 10,
              ...(menuMinWidth !== undefined || menuMaxWidth !== undefined
                ? {
                    minWidth: menuMinWidth ?? 200,
                    ...(menuMaxWidth !== undefined ? { maxWidth: menuMaxWidth } : {})
                  }
                : { width: 200 }),
              ...(menuMaxHeight !== undefined ? { maxHeight: menuMaxHeight } : {})
            }
          }
        }}
      >
        {options.map((option, index) => {
          if ((option as any).type === 'group') {
            return (
              <Typography
                key={`group-${index}`}
                variant="overline"
                color="text.secondary"
                sx={{ px: 3, py: 1 }}
              >
                {option.name}
              </Typography>
            )
          }

          const menuItem = (
              <MenuItem
                onClick={() => handleOptionSelect(option)}
                disabled={option.disable}
                selected={!multiple && isOptionSelected(option)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: multiple ? 'transparent' : '#07004D29',
                    '&:hover': {
                      backgroundColor: multiple ? 'grey.50' : '#00000005'
                    }
                  },
                  opacity: option.disable ? 0.5 : 1,
                  px: multiple ? 2 : 2,
                  py: 1,
                  ...(wrapOptionText ? { whiteSpace: 'normal' } : {}),
                  '&:hover': { backgroundColor: 'grey.50' }
                }}
              >
                {multiple ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Checkbox
                      checked={isOptionSelected(option)}
                      size="medium"
                      sx={{ '& .MuiSvgIcon-root': { fontSize: '18px' } }}
                    />
                    <Typography
                      variant="menuItemDefault"
                      sx={{
                        ml: 1,
                        flex: 1,
                        ...(wrapOptionText ? { wordBreak: 'break-word' } : {})
                      }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                ) : (
                  <Typography
                    variant="menuItemDefault"
                    sx={wrapOptionText ? { wordBreak: 'break-word' } : undefined}
                  >
                    {option.name}
                  </Typography>
                )}
              </MenuItem>
          )

          return (
            <Box key={option.id}>
              {option.description ? (
                <Tooltip title={option.description} arrow placement="right">
                  {/* A disabled MenuItem swallows pointer events, so the
                      tooltip never fires. Wrap it in a non-disabled span so
                      hover is still detected. */}
                  {option.disable ? (
                    <Box component="span" sx={{ display: 'block' }}>
                      {menuItem}
                    </Box>
                  ) : (
                    menuItem
                  )}
                </Tooltip>
              ) : (
                menuItem
              )}
            </Box>
          )
        })}

        {options.length === 0 && (
          <MenuItem disabled>
            <Typography variant="menuItemDefault" color="text.secondary">
              {emptyText}
            </Typography>
          </MenuItem>
        )}

        {menuFooter && (
          <Box
            sx={{ borderTop: '1px solid', borderColor: 'grey.200', px: 2, py: 1.5 }}
            // Keep clicks on the footer controls from bubbling into the
            // Menu's item-selection handling.
            onClick={e => e.stopPropagation()}
            // Let the Menu keep handling dismissal/navigation keys (so
            // keyboard users can still Escape/Tab/arrow out of the footer);
            // only swallow other keys that would trigger MenuList type-ahead.
            onKeyDown={e => {
              const navKeys = ['Escape', 'Tab', 'ArrowUp', 'ArrowDown', 'Home', 'End']
              if (!navKeys.includes(e.key)) e.stopPropagation()
            }}
          >
            {menuFooter}
          </Box>
        )}
      </Menu>
    </Box>
  )
}

export default SelectFilter
